import { Navigate, useParams } from "react-router-dom";
import { Box, Center, Spinner, Text, useBreakpointValue } from "@chakra-ui/react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSuggestionFeed } from "./hooks/useSuggestionFeed";
import { useFeedStreak } from "./hooks/useFeedStreak";
import { FeedSlot } from "./FeedSlot";
import { FeedHeader } from "./FeedHeader";
import {
  FeedSectionHeader,
  getSectionHeader,
} from "./FeedSectionHeader";
import { FeedItem } from "./types";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";
import { useAccountData } from "../../hooks/useAccountData";
import {
  FeedExpandProvider,
  useFeedExpand,
} from "./feedExpandContext";
import { FeedFullscreenPreview } from "./FeedFullscreenPreview";

// Comic features track typical page aspect (~2:3) so the page art fills
// the slot without dead-space top/bottom. Short videos (9:16) want more
// height — they get the larger pcts.
const COMIC_FEATURE_PCT_MOBILE = 0.74;
const COMIC_FEATURE_PCT_DESKTOP = 0.82;
const SHORT_FEATURE_PCT_MOBILE = 0.86;
const SHORT_FEATURE_PCT_DESKTOP = 0.92;
// Uniform spacing between every slot. Earlier we tried a section break
// before each comic_release but consecutive features ended up too far
// apart; flat gap reads better.
const GAP_PX = 24;
const PREFETCH_LOOKAHEAD = 3;

const COMPACT_HEIGHT_PX: Record<string, number> = {
  // Hero-image cards lean tall so the art has room to breathe. Cluster
  // slots (top_comment, rss_section, creator_highlight) are taller because
  // they stack 2–3 rows internally. Streak is the exception — a
  // celebration moment, not a content card.
  comic_recommendation: 300,
  genre_dive: 300,
  rss_section: 520,
  long_video: 280,
  top_comment: 540,
  featured_creator: 320,
  creator_highlight: 460,
  bundle: 300,
  streak_milestone: 140,
};
const COMPACT_DEFAULT_PX = 180;

function resolveDisplay(item: FeedItem): "feature" | "compact" {
  if (item.display) return item.display;
  if (item.type === "comic_release") return "feature";
  return "compact";
}

function getSlotHeightPx(
  item: FeedItem,
  viewportPx: number,
  isMobile: boolean,
): number {
  if (resolveDisplay(item) === "feature") {
    if (item.type === "short_video") {
      const pct = isMobile ? SHORT_FEATURE_PCT_MOBILE : SHORT_FEATURE_PCT_DESKTOP;
      return Math.round(viewportPx * pct);
    }
    const pct = isMobile ? COMIC_FEATURE_PCT_MOBILE : COMIC_FEATURE_PCT_DESKTOP;
    return Math.round(viewportPx * pct);
  }
  return COMPACT_HEIGHT_PX[item.type] ?? COMPACT_DEFAULT_PX;
}

export function SuggestionFeed() {
  return (
    <FeedExpandProvider>
      <SuggestionFeedInner />
    </FeedExpandProvider>
  );
}

function SuggestionFeedInner() {
  const params = useParams();
  const slug = params.slug ?? null;
  const { target: expandTarget, close: closeExpand } = useFeedExpand();
  const containerRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const topBarHeight = useTopBarHeight().get();
  const accountData = useAccountData();
  const flagEnabled = accountData?.features?.["suggestion_feed"];
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const { items: serverItems, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useSuggestionFeed(flagEnabled ? slug : null);
  const { streak, milestone } = useFeedStreak();
  const isAuthed = !!accountData?.user;

  // Inject a streak slot right after the seed feature on every authed feed
  // so the user always sees their current streak count and the visual heat
  // around it. The `milestone` value (newly-crossed threshold, if any) is
  // forwarded for richer celebration copy when relevant; the card renders
  // a generic "current streak" treatment otherwise.
  const items = useMemo<FeedItem[]>(() => {
    if (!isAuthed || streak <= 0 || serverItems.length === 0) {
      return serverItems;
    }
    const out = [...serverItems];
    const insertAt = Math.min(1, out.length);
    out.splice(insertAt, 0, {
      type: "streak_milestone",
      display: "compact",
      streak,
      milestone: milestone ?? streak,
    });
    return out;
  }, [serverItems, streak, milestone, isAuthed]);

  // "Active" slot — the one currently most visible in the viewport. Drives
  // URL mirror, prefetch lookahead, and slot-internal active behavior
  // (engagement overlay on comic, autoplay on video).
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewportPx, setViewportPx] = useState(0);
  const hasItems = items.length > 0;

  useEffect(() => {
    const update = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      setViewportPx(Math.max(0, Math.round(h - topBarHeight)));
    };
    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, [topBarHeight]);

  const setSlotRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      if (el) slotRefs.current.set(index, el);
      else slotRefs.current.delete(index);
    },
    [],
  );

  // Active-slot detection via IntersectionObserver on the scroll container.
  // The slot whose center is closest to the viewport center wins.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    if (!hasItems) return;

    const ratios = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idxAttr = (entry.target as HTMLElement).dataset.slotIndex;
          if (!idxAttr) continue;
          const idx = Number(idxAttr);
          ratios.set(idx, entry.intersectionRatio);
        }
        // Pick highest-ratio slot.
        let bestIdx = 0;
        let bestRatio = -1;
        for (const [idx, r] of ratios) {
          if (r > bestRatio) {
            bestRatio = r;
            bestIdx = idx;
          }
        }
        if (bestRatio > 0) {
          setActiveIndex(bestIdx);
        }
      },
      {
        root,
        // Multiple thresholds so we get smooth transitions as slots scroll
        // through. 0.5 alone is too coarse for tall feature slots.
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    for (const el of slotRefs.current.values()) {
      observer.observe(el);
    }
    return () => observer.disconnect();
  }, [hasItems, items.length]);

  useEffect(() => {
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      items.length > 0 &&
      activeIndex >= items.length - PREFETCH_LOOKAHEAD
    ) {
      fetchNextPage();
    }
  }, [
    activeIndex,
    items.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // Horizontal swipe → comic page nav. Listeners are passive; vertical
  // scrolling stays native (touchAction: pan-y on the container). We only
  // fire when the gesture is horizontal-dominant and over the threshold.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const HSWIPE_THRESHOLD = 50;
    let touchStart: { x: number; y: number } | null = null;
    let pointerStart: { x: number; y: number; id: number } | null = null;
    let pointerDragged = false;

    const fireHorizontal = (dx: number, dy: number) => {
      if (Math.abs(dx) <= Math.abs(dy)) return;
      if (Math.abs(dx) < HSWIPE_THRESHOLD) return;
      // Pass the raw swipe direction; the active slot's viewer maps left/right
      // → reading-order next/prev based on its own reading direction (LTR/RTL).
      window.dispatchEvent(
        new CustomEvent("feed-page-nav", {
          detail: dx > 0 ? "right" : "left",
        }),
      );
    };

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      touchStart = t ? { x: t.clientX, y: t.clientY } : null;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (!touchStart) return;
      const t = e.changedTouches[0];
      const endX = t?.clientX ?? touchStart.x;
      const endY = t?.clientY ?? touchStart.y;
      fireHorizontal(endX - touchStart.x, endY - touchStart.y);
      touchStart = null;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest('a, button, [role="button"], input, textarea')) return;
      pointerStart = { x: e.clientX, y: e.clientY, id: e.pointerId };
      pointerDragged = false;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pointerStart || e.pointerId !== pointerStart.id) return;
      if (
        Math.abs(e.clientX - pointerStart.x) > 6 ||
        Math.abs(e.clientY - pointerStart.y) > 6
      ) {
        pointerDragged = true;
      }
    };
    const onPointerUp = (e: PointerEvent) => {
      if (!pointerStart || e.pointerId !== pointerStart.id) return;
      if (pointerDragged) {
        const dx = e.clientX - pointerStart.x;
        const dy = e.clientY - pointerStart.y;
        fireHorizontal(dx, dy);
        (window as { __feedJustDragged?: number }).__feedJustDragged =
          Date.now();
      }
      pointerStart = null;
      pointerDragged = false;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [hasItems]);

  // Keyboard nav: native ArrowDown/Up moves by ~40px on most browsers — too
  // small for our slot heights. Override to scroll by viewport-ish jumps so
  // arrows feel like "next slot" without forcing snap.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "PageDown") {
        e.preventDefault();
        root.scrollBy({ top: viewportPx, behavior: "smooth" });
      } else if (e.key === "PageUp") {
        e.preventDefault();
        root.scrollBy({ top: -viewportPx, behavior: "smooth" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewportPx]);

  const slotHeightsPx = useMemo(
    () => items.map((item) => getSlotHeightPx(item, viewportPx, isMobile)),
    [items, viewportPx, isMobile],
  );

  if (accountData && !flagEnabled) {
    return <Navigate to="/" replace />;
  }
  if (isLoading) {
    return (
      <Box w="100%" h={`calc(100dvh - ${topBarHeight}px)`} bg="black">
        <Center h="100%">
          <Spinner color="orange.400" size="xl" />
        </Center>
      </Box>
    );
  }
  if (!items.length) {
    return (
      <Box w="100%" h={`calc(100dvh - ${topBarHeight}px)`} bg="black">
        <Center h="100%">
          <Text color="white">No suggestions available</Text>
        </Center>
      </Box>
    );
  }

  return (
    <>
    <Box
      ref={containerRef}
      position="relative"
      w="100%"
      h={`calc(100dvh - ${topBarHeight}px)`}
      bg="black"
      overflowY="auto"
      overflowX="hidden"
      sx={{
        overscrollBehaviorY: "contain",
        // pan-y lets the browser own vertical scroll; we still see horizontal
        // touches via passive listeners for comic page nav.
        touchAction: "pan-y",
      }}
    >
      <Box display="flex" flexDirection="column" sx={{ gap: `${GAP_PX}px` }}>
        <FeedHeader />
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          const key = slotKey(item, index);
          // Section header opens each rail / cluster slot so the user can
          // see "Readers are saying" or "More like this" at a glance.
          const header = getSectionHeader(item);
          return (
            <Fragment key={key}>
              {header ? <FeedSectionHeader {...header} /> : null}
              <Box
                ref={setSlotRef(index)}
                data-slot-index={index}
                h={`${slotHeightsPx[index]}px`}
                w="100%"
                position="relative"
                flexShrink={0}
                px={3}
              >
                <FeedSlot
                  item={item}
                  isActive={isActive}
                  onAdvanceFeed={NOOP}
                  onRetreatFeed={NOOP}
                  hasFeedPrev={index > 0}
                  hasFeedNext={index < items.length - 1 || !!hasNextPage}
                />
              </Box>
            </Fragment>
          );
        })}
      </Box>
    </Box>
    {expandTarget ? (
      <FeedFullscreenPreview
        releaseUuid={expandTarget.releaseUuid ?? null}
        slug={expandTarget.slug ?? null}
        readingDirection={expandTarget.readingDirection}
        initialSlotIndex={expandTarget.initialSlotIndex}
        onClose={closeExpand}
      />
    ) : null}
    </>
  );
}

const NOOP = () => {};

function slotKey(item: FeedItem, index: number): string {
  if (item.type === "comic_release") {
    return `c-${item.comic.id}-${item.release_uuid}`;
  }
  if (item.type === "rss_section") {
    const head = item.articles[0]?.rss_id ?? index;
    return `r-${head}`;
  }
  if (item.type === "top_comment") {
    const head = item.comments[0]?.comment_id ?? index;
    return `t-${head}`;
  }
  if (item.type === "creator_highlight") {
    const head = item.creators[0]?.artist_id ?? index;
    return `ch-${head}`;
  }
  return `u-${index}-${(item as { type: string }).type}`;
}
